import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { overviewApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import {
  formatNumber,
  formatDate,
  formatDeltaPercent,
  getDeltaDirection,
} from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';
import { ComingSoon } from '../components/ComingSoon.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business UXメトリクスの全体概要。ユニークユーザー・セッション・月額登録・購入のKPIとトレンドチャートを表示
 * @graph-connects overviewApi [calls] Overview APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects formatDeltaPercent [calls] 差分パーセントフォーマット
 * @graph-connects getDeltaDirection [calls] 差分方向判定
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 * @graph-connects ComingSoon [calls] Coming Soonオーバーレイ描画
 */
export const Route = createFileRoute('/overview')({
  component: OverviewPage,
});

/** @graph-connects none */
const TREND_METRICS = ['uniqueUsers', 'monthlyRegistrations', 'purchases'];

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function OverviewPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');

  const kpiQuery = useQuery({
    queryKey: queryKeys.overview.kpi(),
    queryFn: () => overviewApi.getKpi(),
    staleTime: 5 * 60 * 1000,
  });

  // トレンドは当日を除外（KPI カードで当日は表示済み）
  const trendsEndDate = (() => {
    const today = new Date();
    const jst = new Date(today.getTime() + 9 * 60 * 60 * 1000);
    const yesterday = new Date(jst);
    yesterday.setDate(yesterday.getDate() - 1);
    const yStr = yesterday.toISOString().slice(0, 10);
    return endDate > yStr ? yStr : endDate;
  })();

  const trendsQuery = useQuery({
    queryKey: queryKeys.overview.trends(startDate, trendsEndDate, TREND_METRICS),
    queryFn: () => overviewApi.getTrends(startDate, trendsEndDate, TREND_METRICS),
    staleTime: 5 * 60 * 1000,
  });

  const kpi = kpiQuery.data;

  return (
    <div className="space-y-6">
      {/* ページタイトル */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('overview.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">{t('overview.description')}</p>
      </div>

      <ComingSoon>
        <div className="space-y-6">
          {/* 本日の KPI カード */}
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] mb-3">
              {t('overview.todayKpi')}
              {kpi && (
                <span className="ml-2 font-normal text-[var(--color-text-muted)]">{kpi.date}</span>
              )}
            </h2>
            {kpiQuery.isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="skeleton-shimmer h-24 rounded-lg" />
                ))}
              </div>
            ) : kpi ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                  label={t('overview.todayUsers')}
                  value={formatNumber(kpi.uniqueUsers.value)}
                  delta={kpi.uniqueUsers.deltaPercent}
                />
                <KpiCard
                  label={t('overview.todaySessions')}
                  value={formatNumber(kpi.uniqueSessions.value)}
                  delta={kpi.uniqueSessions.deltaPercent}
                />
                <KpiCard
                  label={t('overview.monthlyRegistrations')}
                  value={formatNumber(kpi.monthlyRegistrations.value)}
                  delta={kpi.monthlyRegistrations.deltaPercent}
                />
                <KpiCard
                  label={t('overview.purchases')}
                  value={formatNumber(kpi.purchases.value)}
                  delta={kpi.purchases.deltaPercent}
                />
              </div>
            ) : null}
          </div>

          {/* トレンドチャート */}
          <div className="card animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('overview.trendChart')}
              </h3>
              <DateRangeSelector
                preset={preset}
                startDate={startDate}
                endDate={endDate}
                onPresetChange={setPreset}
                onCustomDatesChange={setCustomDates}
              />
            </div>
            {trendsQuery.isLoading ? (
              <div className="skeleton-shimmer h-64 w-full rounded-lg" />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={trendsQuery.data?.data ?? []}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradRegistrations" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--chart-2)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--chart-2)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradPurchases" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--chart-3)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--chart-3)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                      axisLine={{ stroke: 'var(--color-border)' }}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    {/* 左軸: 登録・購入（小さい数値） */}
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) => formatNumber(v)}
                      width={50}
                    />
                    {/* 右軸: ユニークユーザー（大きい数値） */}
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) => formatNumber(v)}
                      width={50}
                    />
                    <Tooltip
                      labelFormatter={formatDate as never}
                      contentStyle={{
                        backgroundColor: 'var(--color-bg-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="uniqueUsers"
                      stroke="var(--chart-1)"
                      strokeWidth={1.5}
                      fill="url(#gradUsers)"
                      dot={false}
                      opacity={0.5}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="monthlyRegistrations"
                      stroke="var(--chart-2)"
                      strokeWidth={2}
                      fill="url(#gradRegistrations)"
                      dot={false}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="purchases"
                      stroke="var(--chart-3)"
                      strokeWidth={2}
                      fill="url(#gradPurchases)"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </ComingSoon>
    </div>
  );
}

/** @graph-connects none */
function KpiCard({ label, value, delta }: { label: string; value: string; delta: number | null }) {
  const direction = delta !== null ? getDeltaDirection(delta) : 'neutral';
  const colorClass =
    direction === 'positive'
      ? 'text-[var(--color-success)]'
      : direction === 'negative'
        ? 'text-[var(--color-error)]'
        : 'text-[var(--color-text-muted)]';

  return (
    <div className="card animate-fade-in-up">
      <p className="text-xs text-[var(--color-text-muted)] mb-1">{label}</p>
      <p className="text-2xl font-bold text-[var(--color-text-primary)]">{value}</p>
      {delta !== null && (
        <p className={`text-xs mt-1 ${colorClass}`}>{formatDeltaPercent(delta)} vs 前日</p>
      )}
    </div>
  );
}
